import { api } from "@/core/api";
import { authApi, authSlice } from "@/core/auth/services";
import { clearCredentials } from "@/core/auth/services/authActions";
import { cameraApi } from "@/core/cameraApi";
import "@/core/cameraApi/listener";
import { organizationApi } from "@/modules/account/services";
import { confidenceReportVisibilitySlice } from "@/modules/analytics/services/confidenceReportVisibilitySlice";
import { cameraSelectionSlice } from "@/modules/camera-selection/services/cameraSelectionSlice";
import { cameraPipelineGenerationSlice } from "@/modules/camera-stream/services/cameraPipelineGenerationSlice";
import { captureApi } from "@/modules/capture/services/captureApi";
import { pendingCapturesSlice } from "@/modules/capture/services/pendingCapturesSlice";
import { pendingVideoCapturesSlice } from "@/modules/capture/services/pendingVideoCapturesSlice";
import { dashboardConfigApi } from "@/modules/dashboard/services";
import { relaySlice } from "@/modules/device/services/relaySlice";
import { modelApi } from "@/modules/model/services";
import { projectApi } from "@/modules/project/services/projectApi";
import { projectSlice } from "@/modules/project/services/projectSlice";
import { combineReducers, configureStore, UnknownAction } from "@reduxjs/toolkit";
import { listenerMiddleware } from "./listenerMiddleware";

const slices = {
  [authSlice.name]: authSlice.reducer,
  [projectSlice.name]: projectSlice.reducer,
  [pendingCapturesSlice.name]: pendingCapturesSlice.reducer,
  [pendingVideoCapturesSlice.name]: pendingVideoCapturesSlice.reducer,
  [cameraSelectionSlice.name]: cameraSelectionSlice.reducer,
  [cameraPipelineGenerationSlice.name]: cameraPipelineGenerationSlice.reducer,
  [confidenceReportVisibilitySlice.name]: confidenceReportVisibilitySlice.reducer,
  [relaySlice.name]: relaySlice.reducer,
};
const apis = {
  [api.reducerPath]: api.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [cameraApi.reducerPath]: cameraApi.reducer,
  [captureApi.reducerPath]: captureApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [projectApi.reducerPath]: projectApi.reducer,
  [modelApi.reducerPath]: modelApi.reducer,
  [dashboardConfigApi.reducerPath]: dashboardConfigApi.reducer,
};
const middlewares = [
  listenerMiddleware.middleware,
  api.middleware,
  authApi.middleware,
  cameraApi.middleware,
  captureApi.middleware,
  organizationApi.middleware,
  projectApi.middleware,
  modelApi.middleware,
  dashboardConfigApi.middleware,
];

const appReducer = combineReducers({
  ...slices,
  ...apis,
});

// Every way a session can end, so nothing from one user survives into the
// next: the logout button dispatches clearCredentials, but the logout mutation
// and a rejected token refresh end the session on their own — and used to clear
// only the auth slice, leaving RTK Query caches (the org list above all) intact
// for whoever logged in next.
const isSessionEnd = (action: UnknownAction) =>
  clearCredentials.match(action) ||
  authApi.endpoints.logout.matchFulfilled(action) ||
  authApi.endpoints.refreshTokens.matchRejected(action);

// Reset all state on session end; the auth slice's own reducers still run on
// the fresh state, so the cleared-credentials flags survive the reset.
const rootReducer: typeof appReducer = (state, action) => {
  if (isSessionEnd(action)) {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(middlewares),
});
